import { ChevronDown, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from './Button';

/**
 * One card of a STACK of groups a form splits its work into — a purchase order per supplier,
 * a plantilla post per unit. Lifted out of the purchase order form so the two stacks are one
 * shape, not two that drift (19 Oct 2026).
 *
 * - **The SPINE is drawn only while the card is OPEN** — the group's subject read sideways down
 *   its edge (`Supplier: …`, `Unit: …`). At a collapsed height it would clip to nothing, so the
 *   HEADER carries the subject while the card is shut: the collapse rule read literally — the
 *   contents go, what names them stays, and it is the way back.
 * - **`Not selected`, never an em dash** — the subject is a decision not yet taken, not a value
 *   the record has none of.
 * - **The chevron renders the STATE** — down when expanded, right when collapsed.
 * - **Remove appears only while there is more than one card** — the caller decides by passing
 *   `onRemove` or not; a stack never empties to nothing.
 * - **The body is UNMOUNTED while collapsed**, so a form refused on Submit must expand every
 *   card before `focusValidationError` runs, or the field it names does not exist.
 */
export function GroupCard({
  id,
  title,
  subjectLabel,
  subject,
  collapsed,
  onToggle,
  summary,
  onRemove,
  removeDisabled = false,
  children,
}: {
  id: string;
  /** The card's ordinal name in the stack, e.g. "Purchase order 2". */
  title: string;
  /** What the group is OF, for the spine — "Supplier", "Unit". */
  subjectLabel: string;
  /** The chosen subject's name, or null while none is chosen. */
  subject: string | null;
  collapsed: boolean;
  onToggle: () => void;
  /** What the header adds after the subject while the card is SHUT — counts, a subtotal. */
  summary?: ReactNode;
  /** Omitted while the card is the only one. */
  onRemove?: () => void;
  removeDisabled?: boolean;
  children: ReactNode;
}) {
  const subjectText = (
    <span className={subject ? 'text-slate-700' : 'text-slate-400'}>{subject ?? 'Not selected'}</span>
  );

  return (
    <div
      className={`relative flex flex-col gap-3 overflow-hidden rounded-lg border border-slate-200 bg-white p-3 ${
        collapsed ? '' : 'pl-10'
      }`}
    >
      {collapsed ? null : (
        <div
          aria-hidden="true"
          className="absolute inset-y-0 left-0 flex w-7 items-center justify-center border-r border-slate-200 bg-slate-50"
        >
          <span className="rotate-180 whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-slate-500 [writing-mode:vertical-rl]">
            {subjectLabel}:{' '}
            {/*
              The subject in the label's own uppercase and a MUTED tone (product call, 19 Oct
              2026) — a spine is a label running down the card, not a value to read first; the
              field beside it is where the subject is chosen and read. Unchosen is lighter still.
            */}
            <span className={`font-medium ${subject ? 'text-slate-500' : 'text-slate-400'}`}>
              {subject ?? 'Not selected'}
            </span>
          </span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          id={`${id}-disclosure`}
          onClick={onToggle}
          aria-expanded={!collapsed}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${collapsed ? '-rotate-90' : ''}`}
            aria-hidden="true"
          />

          <span className="shrink-0 text-xs font-medium text-slate-600">{title}</span>

          {collapsed ? (
            <span className="min-w-0 truncate text-xs text-slate-500">
              · {subjectText}
              {summary}
            </span>
          ) : null}
        </button>

        {onRemove ? (
          <Button
            id={`${id}-remove`}
            variant="tertiary"
            onClick={onRemove}
            disabled={removeDisabled}
            leading={<Trash2 className="h-4 w-4" aria-hidden="true" />}
          >
            Remove
          </Button>
        ) : null}
      </div>

      {collapsed ? null : children}
    </div>
  );
}
